/** @format */

const GOOGLE_ADS_ID = "AW-16995302498";

/**
 * Google Ads conversion labels from Goals → Conversions → Tag setup.
 * Format: string after the slash in `AW-16995302498/LABEL`.
 */
const CONVERSION_LABELS = {
  phone_call: "",
  form_submit: "cQ_zCLiVo7YcEOL4_qc_",
  checkout_start: "",
  purchase: "RICwCIidubYcEOL4_qc_",
};

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];
const GCLID_KEY = "gclid";
const FBCLID_KEY = "fbclid";
const UTM_STORAGE_KEY = "acclaimed_utm_params";

function getGtag(): ((...args: any[]) => void) | null {
  if (typeof window === "undefined") return null;
  const win = window as any;
  if (typeof win.gtag !== "function") return null;
  return win.gtag;
}

function fireConversion(label: string, params: Record<string, any> = {}) {
  if (!label) return;
  const gtag = getGtag();
  if (!gtag) return;
  gtag("event", "conversion", {
    send_to: `${GOOGLE_ADS_ID}/${label}`,
    ...params,
  });
}

function withAttribution(
  params: Record<string, any> = {}
): Record<string, any> {
  return { ...getUtmParams(), ...params };
}

export function captureUtmParams() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const stored = getUtmParams();
  const utm: Record<string, string> = { ...stored };

  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) utm[key] = val;
  }

  const gclid = params.get(GCLID_KEY);
  if (gclid) utm[GCLID_KEY] = gclid;

  const fbclid = params.get(FBCLID_KEY);
  if (fbclid) utm[FBCLID_KEY] = fbclid;

  if (Object.keys(utm).length > 0) {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
  }
}

export function getUtmParams(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
  if (!stored) return {};
  try {
    return JSON.parse(stored);
  } catch {
    return {};
  }
}

/** Append stored ad attribution params to outbound links. */
export function appendAttributionParams(url: string): string {
  if (typeof window === "undefined") return url;

  const utm = getUtmParams();
  const keys = Object.keys(utm);
  if (keys.length === 0) return url;

  try {
    const target = new URL(url, window.location.origin);
    for (const key of keys) {
      if (!target.searchParams.has(key)) {
        target.searchParams.set(key, utm[key]);
      }
    }
    return target.pathname + target.search + target.hash;
  } catch {
    return url;
  }
}

export function trackPhoneCall() {
  const gtag = getGtag();
  if (!gtag) return;
  gtag("event", "generate_lead", withAttribution({ method: "phone" }));
  fireConversion(CONVERSION_LABELS.phone_call, {
    value: 10.0,
    currency: "USD",
  });
}

export function trackContactFormSubmit() {
  const gtag = getGtag();
  if (!gtag) return;
  gtag("event", "generate_lead", withAttribution({ method: "contact_form" }));
  fireConversion(CONVERSION_LABELS.form_submit, {
    value: 25.0,
    currency: "USD",
  });
}

export function trackCheckoutStart() {
  const gtag = getGtag();
  if (!gtag) return;
  gtag("event", "begin_checkout", withAttribution({ currency: "USD" }));
  fireConversion(CONVERSION_LABELS.checkout_start, {
    value: 50.0,
    currency: "USD",
  });
}

export function trackPurchase(
  value: number,
  transactionId?: string,
  email?: string
) {
  const gtag = getGtag();
  if (!gtag) return;
  gtag(
    "event",
    "purchase",
    withAttribution({
      value,
      currency: "USD",
      transaction_id: transactionId,
    })
  );
  fireConversion(CONVERSION_LABELS.purchase, {
    value,
    currency: "USD",
    transaction_id: transactionId,
  });
  if (email) {
    gtag("set", "user_data", { email });
  }
}

export function trackLandingPageView(page: string) {
  const gtag = getGtag();
  if (!gtag) return;
  gtag("event", "page_view", withAttribution({ page_path: page }));
}

/** Fired when a visitor picks a state and enters the purchase path. */
export function trackStatePlanClick(stateSlug: string) {
  const gtag = getGtag();
  if (!gtag) return;
  gtag(
    "event",
    "select_content",
    withAttribution({
      content_type: "homeowner_plan_state",
      item_id: stateSlug,
    })
  );
}
